import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "March 1, 2026";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    content: `We collect information you provide directly when you create an account, use our Service, or communicate with us. This includes:\n\n- Account information: name, email address, password, organization name\n- Profile information: avatar, job title, timezone preferences\n- Content: tasks, projects, comments, files, and other data you create within the Service\n- Communications: messages you send to us, including support requests and feedback\n- Payment information: billing address and payment method details (processed by our payment provider)\n\nWe also collect information automatically, including:\n\n- Device and browser information (type, operating system, IP address)\n- Usage data (features used, pages visited, timestamps)\n- Cookies and similar tracking technologies`,
  },
  {
    title: "2. How We Use Your Information",
    content: `We use the information we collect to:\n\n- Provide, maintain, and improve the Service\n- Process transactions and send related notices\n- Send technical notices, updates, security alerts, and administrative messages\n- Respond to your comments, questions, and support requests\n- Monitor and analyze trends, usage, and activities\n- Detect, investigate, and prevent fraud and other illegal activities\n- Personalize and improve your experience\n- Develop new features and services based on aggregated, anonymized usage patterns`,
  },
  {
    title: "3. How We Share Your Information",
    content: `We do not sell your personal information. We may share your information in the following circumstances:\n\n- With service providers who assist in operating our Service (hosting, analytics, payment processing)\n- With your organization's administrators if you use the Service through a team or enterprise account\n- When required by law, subpoena, or government request\n- To protect the rights, property, or safety of Opero, our users, or the public\n- In connection with a merger, acquisition, or sale of assets (with prior notice)\n- With your consent or at your direction`,
  },
  {
    title: "4. Data Security",
    content: `We implement industry-standard security measures to protect your data, including:\n\n- Encryption in transit (TLS 1.3) and at rest (AES-256)\n- Regular security audits and penetration testing\n- Access controls and authentication requirements\n- Secure infrastructure hosted on AWS with SOC 2 compliance\n- Incident response procedures and breach notification protocols\n\nWhile we take reasonable measures to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.`,
  },
  {
    title: "5. Cookies and Tracking",
    content: `We use cookies and similar technologies to provide and improve the Service. These include:\n\n- Essential cookies: required for the Service to function (authentication, security)\n- Analytics cookies: help us understand how the Service is used (anonymized)\n- Preference cookies: remember your settings and preferences\n\nYou can control cookie preferences through your browser settings. Disabling essential cookies may affect the functionality of the Service.`,
  },
  {
    title: "6. Your Rights",
    content: `Depending on your location, you may have the following rights regarding your personal data:\n\n- Access: request a copy of the personal data we hold about you\n- Correction: request correction of inaccurate or incomplete data\n- Deletion: request deletion of your personal data\n- Portability: request your data in a structured, machine-readable format\n- Objection: object to processing of your data for certain purposes\n- Restriction: request restriction of processing in certain circumstances\n\nTo exercise these rights, contact us at corporate@digiits.com. We will respond within 30 days.`,
  },
  {
    title: "7. Data Retention",
    content: `We retain your personal data for as long as your account is active or as needed to provide the Service. After account deletion, we retain certain data for up to 90 days for backup and recovery purposes, and may retain anonymized, aggregated data indefinitely for analytics. We retain billing records as required by applicable tax and accounting laws.`,
  },
  {
    title: "8. Children's Privacy",
    content: `The Service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If we learn that we have collected personal information from a child under 16, we will take steps to delete that information promptly.`,
  },
  {
    title: "9. International Data Transfers",
    content: `Your information may be transferred to and processed in countries other than your country of residence, including the Republic of Ghana and countries where our infrastructure providers operate. We ensure appropriate safeguards are in place for international transfers, including Standard Contractual Clauses approved by the relevant authorities, and we comply with Ghana's Data Protection Act, 2012 (Act 843) where applicable.`,
  },
  {
    title: "10. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and, where appropriate, by email. The "Effective date" at the top of this page indicates when the policy was last revised.`,
  },
  {
    title: "11. Contact Us",
    content: `If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:\n\nDigiits\nAccra, Ghana\ncorporate@digiits.com`,
  },
];

export function PrivacyPage() {
  return (
    <main className="pt-28 pb-20">
      <article className="mx-auto max-w-3xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="mt-6 border-b border-slate-200 dark:border-slate-800 pb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          At Opero, we take your privacy seriously. This Privacy Policy explains
          how we collect, use, share, and protect your personal information when
          you use our platform.
        </div>

        <div className="mt-8 space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3">
                {section.content.split("\n\n").map((block, i) => {
                  const trimmed = block.trim();
                  if (trimmed.startsWith("- ")) {
                    const items = trimmed.split("\n").filter((l) => l.trim());
                    return (
                      <ul
                        key={i}
                        className="list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                      >
                        {items.map((item, j) => (
                          <li key={j}>{item.replace(/^-\s*/, "")}</li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p
                      key={i}
                      className="text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                    >
                      {trimmed}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-slate-200 dark:border-slate-800 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </article>
    </main>
  );
}
